import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  Chip,
  Button,
  Skeleton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import FacebookOutlinedIcon from '@mui/icons-material/FacebookOutlined';
import InstagramIcon from '@mui/icons-material/Instagram';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useNavigate } from 'react-router-dom';
import { useAppSettings } from '../context/SettingsContext';
import { fetchMetaPageInsights, type MetaPageInsights } from '../services/metaApi';

const MetaDashboard: React.FC = () => {
  const { settings, updateSettings } = useAppSettings();
  const navigate = useNavigate();
  const meta = settings.integrations.meta;
  const [activeId, setActiveId] = useState(meta.activeAccountId);
  const [insights, setInsights] = useState<Record<string, MetaPageInsights | null>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const out: Record<string, MetaPageInsights | null> = {};
      for (const acc of meta.accounts) {
        if (acc.accessToken.trim() && acc.pageId.trim()) {
          out[acc.id] = await fetchMetaPageInsights(acc).catch(() => null);
        } else {
          out[acc.id] = null;
        }
      }
      if (!cancelled) {
        setInsights(out);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meta.accounts]);

  const configured = meta.accounts.some((a) => a.accessToken.trim() && a.pageId.trim());

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          background: 'linear-gradient(135deg, #1877f2 0%, #833ab4 50%, #fd1d1d 100%)',
          color: '#fff',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
          <FacebookOutlinedIcon sx={{ fontSize: 32 }} />
          <Typography variant="h4" fontWeight={800}>
            Facebook & Instagram
          </Typography>
        </Stack>
        <Typography sx={{ opacity: 0.95, maxWidth: 520 }}>
          Manage multiple pages and Instagram business accounts from one place.
        </Typography>
        <Button
          variant="contained"
          sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
          startIcon={<SettingsOutlinedIcon />}
          onClick={() => navigate('/settings#meta')}
        >
          Manage accounts
        </Button>
      </Box>

      {!configured && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Connect your Meta app and add page access tokens in API Settings → Facebook & Instagram.
        </Alert>
      )}

      <FormControl size="small" sx={{ mb: 3, minWidth: 220 }}>
        <InputLabel>Highlight account</InputLabel>
        <Select
          label="Highlight account"
          value={activeId}
          onChange={(e) => {
            const id = String(e.target.value);
            setActiveId(id);
            updateSettings((s) => ({
              ...s,
              integrations: { ...s.integrations, meta: { ...s.integrations.meta, activeAccountId: id } },
            }));
          }}
        >
          {meta.accounts.map((a) => (
            <MenuItem key={a.id} value={a.id}>
              {a.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Grid container spacing={2}>
        {meta.accounts.map((acc) => {
          const ins = insights[acc.id];
          const isActive = acc.id === activeId;
          return (
            <Grid item xs={12} md={6} key={acc.id}>
              <Card
                variant={isActive ? 'elevation' : 'outlined'}
                sx={{
                  height: '100%',
                  borderColor: isActive ? 'primary.main' : undefined,
                  borderWidth: isActive ? 2 : 1,
                }}
              >
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Typography variant="h6" fontWeight={800}>
                        {acc.label}
                      </Typography>
                      <Stack direction="row" spacing={0.5} mt={0.5}>
                        {(acc.platform === 'facebook' || acc.platform === 'both') && (
                          <Chip size="small" icon={<FacebookOutlinedIcon />} label="Facebook" />
                        )}
                        {(acc.platform === 'instagram' || acc.platform === 'both') && (
                          <Chip size="small" icon={<InstagramIcon />} label="Instagram" />
                        )}
                      </Stack>
                    </Box>
                    {isActive && <Chip size="small" color="primary" label="Active" />}
                  </Stack>
                  {loading ? (
                    <Skeleton height={80} />
                  ) : ins ? (
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Facebook fans
                        </Typography>
                        <Typography variant="h5" fontWeight={800}>
                          {ins.fanCount?.toLocaleString() ?? '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Instagram
                        </Typography>
                        <Typography variant="h5" fontWeight={800}>
                          {ins.instagramUsername ? `@${ins.instagramUsername}` : '—'}
                        </Typography>
                        {ins.instagramFollowers != null && (
                          <Typography variant="caption" color="text.secondary">
                            {ins.instagramFollowers.toLocaleString()} followers
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {acc.pageId.trim() ? 'Could not load insights — check token permissions' : 'Add Page ID and access token in settings'}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default MetaDashboard;
